import { useEffect } from 'react';
import RadioButton from '../atoms/RadioButton';
import { PaymentProvider, PaymentType } from '../../models/payment';
import PayWayCheckout from '../PayWayCheckout';
import NaveCheckout, { NaveCheckoutResult } from '../NaveCheckout';
import { PayWayTokenData } from '../../store/apis/orders';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { useGetEventCardPromotionsQuery } from '../../store';
import type { PublicCardPromotionDTO } from '../../types/CardPromotion';
import config from '../../config/config';

interface PaymentProviderConfig {
  provider: PaymentProvider;
  label: string;
  description: string;
  enabled: boolean;
  promotions?: PublicCardPromotionDTO[];
}

const formatInstallments = (counts: number[]) => {
  const sorted = [...counts].sort((a, b) => a - b);
  if (sorted.length <= 1) {
    return String(sorted[0] ?? '');
  }
  return `${sorted.slice(0, -1).join(', ')} y ${sorted[sorted.length - 1]}`;
};

export const shouldShowSubmitButton = (
  paymentProvider: PaymentProvider | null,
  paymentMethod: PaymentType | null,
  totalToPay: number | undefined,
  isBackoffice: boolean,
  navePaymentRequestId: string | null,
): boolean => {
  const showPayWayCheckout = !!(paymentProvider === PaymentProvider.PAYWAY
    && paymentMethod === PaymentType.ONLINE
    && totalToPay
    && totalToPay > 0
    && !isBackoffice);

  const showNaveCheckout = !!(paymentProvider === PaymentProvider.NAVE
    && paymentMethod === PaymentType.ONLINE
    && totalToPay
    && totalToPay > 0
    && !isBackoffice
    && navePaymentRequestId);

  return !showPayWayCheckout && !showNaveCheckout;
};

interface CheckoutPaymentOptionsProps {
  eventId: string;
  businessId: string;
  paywayPromotionsOnly?: boolean;
  // Billing province the Payway form starts with (the event's province).
  paywayDefaultProvince?: string;
  paymentProvider: PaymentProvider | null;
  paymentMethod: PaymentType | null;
  showManualOption: boolean;
  allowMercadoPago: boolean;
  allowUala: boolean;
  allowPayWay: boolean;
  allowNave: boolean;
  allowNaveLink: boolean;
  allowPaywayLink?: boolean;
  allowZilo: boolean;
  allowStripe: boolean;
  allowFiserv?: boolean;
  // Cuotas del checkout de Fiserv: su página no deja elegirlas, viajan en la orden.
  fiservInstallments?: number;
  onFiservInstallmentsChange?: (installments: number) => void;
  // Promoción bancaria de Fiserv elegida junto con las cuotas (null = sin promo).
  fiservPromotionId?: string | null;
  onFiservPromotionIdChange?: (promotionId: string | null) => void;
  disabled?: boolean;
  onProviderSelect: (provider: PaymentProvider | null, method: PaymentType | null) => void;
  // Props for embedded checkout forms
  totalToPay?: number;
  isBackoffice?: boolean;
  clickedPay?: boolean;
  isPayWayProcessing?: boolean;
  setIsPayWayProcessing?: (processing: boolean) => void;
  handlePayWayTokenCreated?: (tokenData: PayWayTokenData) => void;
  handlePayWayError?: (error: string) => void;
  validatePayWayContact?: () => Promise<boolean>;
  payWayFormError?: string | null;
  clearPayWayFormError?: () => void;
  orderItemData?: Array<{
    name: string;
    last_name: string;
    person_id: string;
  }>;
  navePaymentRequestId?: string | null;
  isNaveProcessing?: boolean;
  setIsNaveProcessing?: (processing: boolean) => void;
  handleNavePaymentComplete?: (result: NaveCheckoutResult) => void;
  handleNaveError?: (error: string) => void;
}

/**
 * CheckoutPaymentOptions - Payment method/provider selection
 *
 * Theme Support: ✅ Full light/dark mode support
 * Colors Used: text-text-secondary for descriptions
 */
const CheckoutPaymentOptions: React.FC<CheckoutPaymentOptionsProps> = ({
  eventId,
  businessId,
  paywayPromotionsOnly = false,
  paywayDefaultProvince = '',
  paymentProvider,
  paymentMethod,
  showManualOption,
  allowMercadoPago,
  allowUala,
  allowPayWay,
  allowNave,
  allowNaveLink,
  allowPaywayLink = false,
  allowZilo,
  allowStripe,
  allowFiserv = false,
  fiservInstallments = 1,
  onFiservInstallmentsChange,
  fiservPromotionId = null,
  onFiservPromotionIdChange,
  disabled = false,
  onProviderSelect,
  totalToPay,
  isBackoffice = false,
  clickedPay = false,
  isPayWayProcessing = false,
  setIsPayWayProcessing,
  handlePayWayTokenCreated,
  handlePayWayError,
  validatePayWayContact,
  payWayFormError = null,
  clearPayWayFormError,
  orderItemData,
  navePaymentRequestId,
  isNaveProcessing = false,
  setIsNaveProcessing,
  handleNavePaymentComplete,
  handleNaveError,
}) => {
  const { paymentsEnabled, platformConfig } = useAppConfig();

  const manualOptionEnabled = showManualOption && platformConfig.manual_payments_enabled;

  const { data: eventPromotions } = useGetEventCardPromotionsQuery(
    { businessId, eventId },
    { skip: !(allowPayWay || allowFiserv) || !businessId || !eventId },
  );
  const activePromotions = (eventPromotions ?? [])
    .filter((promo) => promo.is_active && promo.installments.length > 0);
  const paywayPromotions = activePromotions.filter((promo) => promo.gateway !== 'FISERV');
  const fiservPromotions = activePromotions.filter((promo) => promo.gateway === 'FISERV');

  // Fiserv no deja elegir cuotas en su página: el plan (cuotas y, si aplica,
  // la promoción del banco) se elige acá y viaja en la orden. Cada opción es
  // "promoción:cuotas"; sin promoción, ":cuotas".
  const fiservPlans = [
    ...Array.from({ length: Math.max(config.fiservMaxInstallments, 1) }, (_, i) => ({
      value: `:${i + 1}`,
      label: i === 0 ? '1 pago' : `${i + 1} cuotas`,
    })),
    ...fiservPromotions.flatMap((promo) => [...promo.installments].sort((a, b) => a - b).map((count) => ({
      value: `${promo.id}:${count}`,
      label: `${count} cuotas sin interés · solo tarjetas ${promo.bank}`,
    }))),
  ];
  const selectedFiservPromotion = fiservPromotions.find((promo) => promo.id === fiservPromotionId);
  const handleFiservPlanChange = (value: string) => {
    const [promotionId, count] = value.split(':');
    onFiservInstallmentsChange?.(Number(count));
    onFiservPromotionIdChange?.(promotionId || null);
  };
  const describePromotion = (promo: PublicCardPromotionDTO) => `${formatInstallments(promo.installments)} cuotas sin interés con ${promo.bank}`;
  const paywayLabel = paywayPromotionsOnly
    ? paywayPromotions.map(describePromotion).join(' - ')
    : 'Tarjeta de crédito en cuotas';

  const paymentProviders: PaymentProviderConfig[] = [
    {
      provider: PaymentProvider.MERCADOPAGO,
      label: 'Mercado Pago',
      description: 'Paga con tu cuenta de Mercado Pago o tarjetas asociadas.',
      enabled: allowMercadoPago && platformConfig.mercadopago_payments_enabled,
    },
    {
      provider: PaymentProvider.UALA,
      label: 'Ualá',
      description: 'Paga con tu billetera Ualá.',
      enabled: allowUala && platformConfig.uala_payments_enabled,
    },
    {
      provider: PaymentProvider.PAYWAY,
      label: paywayLabel,
      description: 'Paga directamente con tarjeta de crédito en cuotas.',
      enabled: allowPayWay && platformConfig.payway_payments_enabled
        && (!paywayPromotionsOnly || paywayPromotions.length > 0),
      promotions: paywayPromotionsOnly ? [] : paywayPromotions,
    },
    {
      provider: PaymentProvider.NAVE,
      label: 'Nave (Naranja X)',
      description: 'Paga con Naranja X o tarjetas asociadas.',
      enabled: false,
    },
    {
      provider: PaymentProvider.NAVE_LINK,
      label: 'Tarjeta de crédito / débito / app bancaria',
      description: 'Serás redirigido para completar el pago.',
      enabled: allowNave && allowNaveLink && platformConfig.nave_payments_enabled,
    },
    {
      provider: PaymentProvider.PAYWAY_LINK,
      label: 'Tarjeta de crédito o débito (Payway)',
      description: 'Serás redirigido a Payway para completar el pago.',
      enabled: allowPaywayLink && platformConfig.payway_payments_enabled,
    },
    {
      provider: PaymentProvider.ZILO,
      label: 'Transferencia',
      description: 'Transferí el dinero a un CVU y recibí tus entradas al instante.',
      enabled: allowZilo && platformConfig.zilo_payments_enabled,
    },
    {
      provider: PaymentProvider.STRIPE,
      label: 'Tarjeta internacional (Stripe)',
      description: 'Serás redirigido a Stripe para pagar con tarjeta de crédito o débito.',
      enabled: allowStripe && platformConfig.stripe_payments_enabled,
    },
    {
      provider: PaymentProvider.FISERV,
      label: 'Tarjeta de crédito o débito',
      description: 'Serás redirigido a Fiserv para pagar con tu tarjeta.',
      enabled: allowFiserv && platformConfig.fiserv_payments_enabled,
      promotions: fiservPromotions,
    },
  ];

  // Global kill-switch or every method globally/event disabled: nothing to offer
  const hasEnabledOption = paymentProviders.some((p) => p.enabled) || manualOptionEnabled;
  const paymentsUnavailable = !!(
    totalToPay
    && totalToPay > 0
    && !isBackoffice
    && (!paymentsEnabled || !hasEnabledOption)
  );

  const handleProviderSelect = (provider: PaymentProvider, method: PaymentType) => {
    onProviderSelect(provider, method);
  };

  const handleManualSelect = () => {
    onProviderSelect(null, PaymentType.MANUAL);
  };

  // Auto-select if there's only one payment option available
  useEffect(() => {
    // Only auto-select if nothing is currently selected
    if (paymentsUnavailable || paymentProvider !== null || paymentMethod !== null) {
      return;
    }

    const enabledProviders = paymentProviders.filter((p) => p.enabled);
    const totalOptions = enabledProviders.length + (manualOptionEnabled ? 1 : 0);

    // If there's exactly one option, auto-select it
    if (totalOptions === 1) {
      if (manualOptionEnabled && enabledProviders.length === 0) {
        // Only manual option available
        handleManualSelect();
      } else if (enabledProviders.length === 1) {
        // Only one provider available
        handleProviderSelect(enabledProviders[0].provider, PaymentType.ONLINE);
      }
    }
  }, [
    paymentProvider,
    paymentMethod,
    allowMercadoPago,
    allowUala,
    allowPayWay,
    allowNave,
    allowNaveLink,
    allowPaywayLink,
    allowZilo,
    allowStripe,
    allowFiserv,
    showManualOption,
    platformConfig,
    paymentsUnavailable,
  ]);

  if (paymentsUnavailable) {
    return (
      <div className="glass-surface rounded-3xl flex flex-col gap-2 p-3 self-stretch">
        <h1 className="text-lg font-semibold">Pagos no disponibles</h1>
        <p className="text-sm text-text-secondary">
          Los pagos en línea están temporalmente deshabilitados. Intentá nuevamente más tarde.
        </p>
      </div>
    );
  }

  const title = manualOptionEnabled ? 'Método de pago' : 'Medio de pago';
  const descriptionSize = manualOptionEnabled ? 'text-xs' : 'text-sm';

  // Computed values for conditional rendering
  const showPayWayCheckout = !!(paymentProvider === PaymentProvider.PAYWAY
    && paymentMethod === PaymentType.ONLINE
    && totalToPay
    && totalToPay > 0
    && !isBackoffice);

  const showNaveCheckout = !!(paymentProvider === PaymentProvider.NAVE
    && paymentMethod === PaymentType.ONLINE
    && totalToPay
    && totalToPay > 0
    && !isBackoffice
    && navePaymentRequestId);

  return (
    <>
      <div className="glass-surface rounded-3xl flex flex-col gap-4 p-3 self-stretch">
        <h1 className="text-lg font-semibold">{title}</h1>
        <div className="flex flex-col gap-3">
          {paymentProviders
            .filter((p) => p.enabled)
            .map((providerConfig) => (
              <div className="flex flex-col" key={providerConfig.provider}>
                <RadioButton
                  name="paymentProvider"
                  value={providerConfig.provider}
                  checked={paymentProvider === providerConfig.provider}
                  label={providerConfig.label}
                  disabled={disabled}
                  onChange={() => handleProviderSelect(providerConfig.provider, PaymentType.ONLINE)}
                />
                <span className={`${descriptionSize} text-text-secondary ml-7 ${!manualOptionEnabled ? 'mt-1' : ''}`}>
                  {providerConfig.description}
                </span>
                {providerConfig.provider === PaymentProvider.FISERV
                  && paymentProvider === PaymentProvider.FISERV
                  && fiservPlans.length > 1
                  && onFiservInstallmentsChange && (
                  <div className="ml-7 mt-2 flex flex-col gap-1">
                    <label className="flex items-center gap-2 text-sm">
                      Cuotas
                      <select
                        className="rounded-lg border border-border bg-transparent px-2 py-1"
                        value={`${selectedFiservPromotion ? selectedFiservPromotion.id : ''}:${fiservInstallments}`}
                        disabled={disabled}
                        onChange={(e) => handleFiservPlanChange(e.target.value)}
                      >
                        {fiservPlans.map((plan) => (
                          <option key={plan.value} value={plan.value}>{plan.label}</option>
                        ))}
                      </select>
                    </label>
                    {selectedFiservPromotion && (
                      <span className="text-xs text-text-secondary">
                        {`Pagá con una tarjeta de ${selectedFiservPromotion.bank}: con otra tarjeta el pago se rechaza.`}
                      </span>
                    )}
                  </div>
                )}
                {!!providerConfig.promotions?.length && (
                  <div className="ml-7 mt-2 flex flex-wrap gap-1.5">
                    {providerConfig.promotions.map((promo) => (
                      <span
                        key={promo.id}
                        className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-success-500/30 bg-success-500/10 py-1 pl-1.5 pr-2.5 text-[11px] font-medium leading-tight text-success-950"
                      >
                        <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-success-500 px-1.5 py-[1px] text-[10px] font-bold text-white">
                          {formatInstallments(promo.installments)}
                        </span>
                        <span>
                          cuotas sin interés con
                          {' '}
                          <strong className="font-semibold">{promo.bank}</strong>
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          {manualOptionEnabled && (
            <div className="flex flex-col">
              <RadioButton
                name="paymentMethod"
                value="manual"
                checked={paymentMethod === PaymentType.MANUAL}
                label="Transferencia"
                disabled={disabled}
                onChange={handleManualSelect}
              />
              <span className="text-xs text-text-secondary ml-7">
                Enviarás un mensaje por WhatsApp a una operadora que te indicará como realizar el pago.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* PayWay Credit Card Form */}
      {showPayWayCheckout && handlePayWayTokenCreated && handlePayWayError && setIsPayWayProcessing && orderItemData && (
        <PayWayCheckout
          eventId={eventId}
          promotionsOnly={paywayPromotionsOnly}
          defaultProvince={paywayDefaultProvince}
          amount={Math.round(totalToPay * 100)}
          onTokenCreated={handlePayWayTokenCreated}
          onError={handlePayWayError}
          onBeforeSubmit={validatePayWayContact}
          onProcessing={setIsPayWayProcessing}
          disabled={clickedPay || isPayWayProcessing}
          buttonText="Pagar con tarjeta"
          formError={payWayFormError}
          onClearFormError={clearPayWayFormError}
          orderItems={orderItemData.map((item) => ({
            name: item.name,
            last_name: item.last_name,
            person_id: item.person_id,
          }))}
        />
      )}

      {/* Nave Checkout Form */}
      {showNaveCheckout && handleNavePaymentComplete && handleNaveError && setIsNaveProcessing && (
        <NaveCheckout
          paymentRequestId={navePaymentRequestId}
          onPaymentComplete={handleNavePaymentComplete}
          onError={handleNaveError}
          onProcessing={setIsNaveProcessing}
          disabled={clickedPay || isNaveProcessing}
        />
      )}
    </>
  );
};

export default CheckoutPaymentOptions;
